/**
 * reteContattiService — accesso dati della RETE CONTATTI (personal CRM del
 * super utente, console.yumaos.com).
 *
 * Pattern console: letture/scritture Firestore DIRETTE (la console è solo
 * super_admin e le rules bloccano chiunque altro), come `platformCrmService`.
 * Passano da callable SOLO le due operazioni che non possono stare sul client:
 *  - `analizzaNotaVocaleRete` — chiave/accesso al modello AI, lato server;
 *  - `fondiTagRete` — riscrittura in massa dei riferimenti, va atomica.
 *
 * La logica anti-duplicato (slug, alias, similarità) vive nel modulo PURO
 * `reteTassonomia.ts` — qui si usa, non si riscrive.
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 */

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
// Modulo PURO condiviso con le Cloud Functions (`shared/rete`): la
// normalizzazione anti-duplicato deve essere IDENTICA sui due lati.
import { normalizzaAlias, slugTag } from '@shared/rete/reteTassonomia';
// Stesso modulo per i numeri: la regola dei doppioni è quella del server e della migrazione.
import { telefoniPuliti } from '@shared/rete/telefoniRete';
import type {
  AnalizzaNotaVocaleRequest,
  CollegamentoRete,
  AnalizzaNotaVocaleResponse,
  CercaDossierWebRequest,
  CercaDossierWebResponse,
  DossierWeb,
  FaccettaRete,
  FaccettaReteWriteInput,
  FondiTagRequest,
  FondiTagResponse,
  InterazioneRete,
  InterazioneReteWriteInput,
  PersonaRete,
  PersonaReteWriteInput,
  TagRete,
  TagReteWriteInput,
} from '../types/reteContatti';

const FACCETTE_COLLECTION = 'platform_rete_faccette';
const TAG_COLLECTION = 'platform_rete_tag';
const PERSONE_COLLECTION = 'platform_rete_persone';
const INTERAZIONI_SUBCOLLECTION = 'interazioni';

// ============================================================================
// HELPER — stessi del CRM di piattaforma (mai whitelist: spread + override id)
// ============================================================================

function mapSnap<T>(d: QueryDocumentSnapshot<DocumentData>): T {
  return { ...(d.data() as Record<string, unknown>), id: d.id } as T;
}

function tsMillis(value: unknown): number {
  return value instanceof Timestamp ? value.toMillis() : 0;
}

/** Rimuove le chiavi `undefined` (Firestore le rifiuta). */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

// ============================================================================
// FACCETTE
// ============================================================================

/** Faccette ordinate per `ordine` crescente (l'ordine della UI e del prompt). */
export async function listFaccette(): Promise<FaccettaRete[]> {
  const snap = await getDocs(collection(db, FACCETTE_COLLECTION));
  const items = snap.docs.map((d) => mapSnap<FaccettaRete>(d));
  items.sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0));
  return items;
}

/**
 * Crea o aggiorna una faccetta. Lo slug è derivato dall'etichetta al primo
 * salvataggio e poi NON cambia (è referenziato dai tag): per rinominare si passa
 * l'`id` esistente e si cambia solo l'etichetta.
 */
export async function saveFaccetta(
  input: FaccettaReteWriteInput,
  id?: string,
): Promise<string> {
  const slug = id ?? slugTag(input.etichetta);
  if (!slug) throw new Error('etichetta della faccetta non valida');
  await setDoc(
    doc(db, FACCETTE_COLLECTION, slug),
    stripUndefined({
      ...input,
      ...(id ? {} : { created_at: serverTimestamp() }),
    }),
    { merge: true },
  );
  return slug;
}

// ============================================================================
// TAG
// ============================================================================

/** Tutti i tag (attivi + proposti), ordinati per etichetta. */
export async function listTag(): Promise<TagRete[]> {
  const snap = await getDocs(collection(db, TAG_COLLECTION));
  const items = snap.docs.map((d) => mapSnap<TagRete>(d));
  items.sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }));
  return items;
}

/**
 * Crea un tag, oppure aggiorna quello esistente.
 *
 * ⚠️ Senza `id` il doc id è `slugTag(etichetta)`: se il tag esiste già, questa
 * chiamata NON ne crea un secondo — aggiorna quello. È la difesa strutturale
 * contro i duplicati, e per questo il chiamante deve passare da `risolviTag`/
 * `trovaTagSimili` PRIMA, per accorgersi dei quasi-duplicati.
 *
 * ⚠️ Con `id` si RINOMINA il tag esistente: il doc id storico viene preservato
 * (i riferimenti sulle schede persona puntano a quello). Senza questo parametro
 * una rinomina creerebbe un secondo documento con lo slug nuovo — esattamente il
 * duplicato che tutto l'impianto serve a evitare. `risolviTag` regge la
 * divergenza id↔etichetta che ne risulta.
 *
 * `conteggio_uso` non viene toccato qui: lo muovono solo le applicazioni di tag
 * alle persone (e la fusione), così non si azzera per sbaglio a ogni rinomina.
 */
export async function saveTag(input: TagReteWriteInput, id?: string): Promise<string> {
  const slug = id ?? slugTag(input.etichetta);
  if (!slug) throw new Error('etichetta del tag non valida');

  const ref = doc(db, TAG_COLLECTION, slug);
  const esistente = await getDoc(ref);

  await setDoc(
    ref,
    stripUndefined({
      etichetta: input.etichetta.trim(),
      faccetta: input.faccetta,
      alias: normalizzaAlias(input.alias ?? [], input.etichetta),
      stato: input.stato,
      motivo_proposta: input.motivo_proposta,
      updated_at: serverTimestamp(),
      ...(esistente.exists()
        ? {}
        : { conteggio_uso: 0, created_at: serverTimestamp() }),
    }),
    { merge: true },
  );
  return slug;
}

/** Promuove un tag proposto ad attivo (decisione umana, mai automatica). */
export async function attivaTag(tagId: string): Promise<void> {
  await updateDoc(doc(db, TAG_COLLECTION, tagId), {
    stato: 'attivo',
    updated_at: serverTimestamp(),
  });
}

/**
 * Aggiunge un alias a un tag esistente (normalizzato, senza duplicati).
 * È il modo giusto di reagire a "l'AI ha proposto un doppione": invece di
 * creare il tag, si insegna al sistema come si chiama anche.
 */
export async function aggiungiAlias(tag: TagRete, nuovoAlias: string): Promise<void> {
  const alias = normalizzaAlias([...(tag.alias ?? []), nuovoAlias], tag.etichetta);
  await updateDoc(doc(db, TAG_COLLECTION, tag.id), {
    alias,
    updated_at: serverTimestamp(),
  });
}

/**
 * Elimina un tag e lo rimuove da tutte le schede che lo portano.
 * Per unire due tag NON si usa questa: si usa `fondiTag` (conserva l'etichetta
 * come alias del vincente).
 */
export async function eliminaTag(tagId: string): Promise<number> {
  const persone = await listPersone();
  const coinvolte = persone.filter((p) => (p.tag ?? []).includes(tagId));

  const batch = writeBatch(db);
  for (const p of coinvolte) {
    batch.update(doc(db, PERSONE_COLLECTION, p.id), {
      tag: (p.tag ?? []).filter((t) => t !== tagId),
      updated_at: serverTimestamp(),
    });
  }
  batch.delete(doc(db, TAG_COLLECTION, tagId));
  await batch.commit();
  return coinvolte.length;
}

// ============================================================================
// PERSONE
// ============================================================================

/** Tutte le persone, le aggiornate di recente per prime. */
export async function listPersone(): Promise<PersonaRete[]> {
  const snap = await getDocs(collection(db, PERSONE_COLLECTION));
  const items = snap.docs.map((d) => mapSnap<PersonaRete>(d));
  items.sort(
    (a, b) =>
      (tsMillis(b.updated_at) || tsMillis(b.created_at)) -
      (tsMillis(a.updated_at) || tsMillis(a.created_at)),
  );
  return items;
}

/** Crea una scheda persona. Solo `nome` è obbligatorio: il resto è rimandabile. */
export async function createPersona(input: PersonaReteWriteInput): Promise<string> {
  const nome = input.nome.trim();
  if (!nome) throw new Error('il nome è obbligatorio');

  const ref = await addDoc(
    collection(db, PERSONE_COLLECTION),
    stripUndefined({
      ...input,
      nome,
      telefoni: telefoniPuliti(input.telefoni),
      tag: input.tag ?? [],
      conteggio_interazioni: 0,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      created_by_uid: auth.currentUser?.uid ?? null,
    }),
  );

  // I tag applicati alla creazione contano subito nell'uso.
  await incrementaUsoTag(input.tag ?? [], []);
  return ref.id;
}

/**
 * Aggiorna una scheda. Se il patch contiene `tag`, i conteggi d'uso vengono
 * riallineati sulla differenza (serve `tagPrecedenti` per sapere cosa è uscito).
 */
export async function updatePersona(
  id: string,
  patch: Partial<PersonaReteWriteInput>,
  tagPrecedenti?: string[],
): Promise<void> {
  const pulito = 'telefoni' in patch ? { ...patch, telefoni: telefoniPuliti(patch.telefoni) } : patch;
  await updateDoc(
    doc(db, PERSONE_COLLECTION, id),
    stripUndefined({ ...pulito, updated_at: serverTimestamp() }),
  );

  if (patch.tag && tagPrecedenti) {
    await incrementaUsoTag(patch.tag, tagPrecedenti);
  }
}

/** Elimina una scheda persona e scala i conteggi d'uso dei suoi tag. */
export async function eliminaPersona(persona: PersonaRete): Promise<void> {
  await deleteDoc(doc(db, PERSONE_COLLECTION, persona.id));
  await incrementaUsoTag([], persona.tag ?? []);
}

/**
 * Riallinea `conteggio_uso` sulla differenza tra i tag nuovi e quelli vecchi.
 * Denormalizzazione volutamente semplice: il valore vero resta ricavabile dalle
 * schede, e la fusione lo ricalcola da zero.
 */
async function incrementaUsoTag(tagDopo: string[], tagPrima: string[]): Promise<void> {
  const aggiunti = tagDopo.filter((t) => !tagPrima.includes(t));
  const rimossi = tagPrima.filter((t) => !tagDopo.includes(t));
  if (!aggiunti.length && !rimossi.length) return;

  const batch = writeBatch(db);
  for (const t of aggiunti) {
    batch.set(doc(db, TAG_COLLECTION, t), { conteggio_uso: increment(1) }, { merge: true });
  }
  for (const t of rimossi) {
    batch.set(doc(db, TAG_COLLECTION, t), { conteggio_uso: increment(-1) }, { merge: true });
  }
  await batch.commit();
}

// ============================================================================
// INTERAZIONI (diario append-only)
// ============================================================================

/** Interazioni di una persona, le più recenti per prime. */
export async function listInterazioni(personaId: string): Promise<InterazioneRete[]> {
  const snap = await getDocs(
    collection(db, PERSONE_COLLECTION, personaId, INTERAZIONI_SUBCOLLECTION),
  );
  const items = snap.docs.map((d) => mapSnap<InterazioneRete>(d));
  // Ordine per data dichiarata (stringa ISO: confronto lessicografico = cronologico),
  // a pari data decide il momento di inserimento.
  items.sort((a, b) => b.quando.localeCompare(a.quando) || tsMillis(b.created_at) - tsMillis(a.created_at));
  return items;
}

/**
 * Aggiunge una voce al diario e aggiorna il contatore denormalizzato sulla
 * scheda. Append-only: le interazioni non si riscrivono, si accumulano — è ciò
 * che renderà possibile calcolare "ultimo contatto" quando arriveranno i richiami.
 */
export async function aggiungiInterazione(
  personaId: string,
  input: InterazioneReteWriteInput,
): Promise<string> {
  const ref = await addDoc(
    collection(db, PERSONE_COLLECTION, personaId, INTERAZIONI_SUBCOLLECTION),
    stripUndefined({
      ...input,
      created_at: serverTimestamp(),
      created_by_uid: auth.currentUser?.uid ?? null,
    }),
  );

  await updateDoc(doc(db, PERSONE_COLLECTION, personaId), {
    conteggio_interazioni: increment(1),
    updated_at: serverTimestamp(),
  });
  return ref.id;
}

// ============================================================================
// CALLABLE
// ============================================================================

/**
 * Manda un racconto (audio o testo) al modello e riceve nota pulita + tag.
 * NON scrive nulla: applicare i tag e salvare l'interazione è una decisione
 * dell'utente, presa sulla schermata di revisione.
 */
export async function analizzaNotaVocale(
  input: AnalizzaNotaVocaleRequest,
): Promise<AnalizzaNotaVocaleResponse> {
  const fn = httpsCallable<AnalizzaNotaVocaleRequest, AnalizzaNotaVocaleResponse>(
    functions,
    'analizzaNotaVocaleRete',
  );
  const result = await fn(stripUndefined({ ...input } as Record<string, unknown>) as AnalizzaNotaVocaleRequest);
  return result.data;
}

/**
 * Unisce due tag: i riferimenti passano al vincente, l'etichetta del perdente
 * diventa un suo alias (così non si ricreerà mai più), il perdente è eliminato.
 * Lato server perché deve riscrivere N schede in modo atomico.
 */
export async function fondiTag(input: FondiTagRequest): Promise<FondiTagResponse> {
  const fn = httpsCallable<FondiTagRequest, FondiTagResponse>(functions, 'fondiTagRete');
  const result = await fn(input);
  return result.data;
}

// ============================================================================
// DOSSIER WEB
// ============================================================================

/**
 * Cerca sul web le informazioni professionali pubbliche su una persona.
 * NON scrive nulla: il risultato si salva solo se l'utente lo conferma
 * (`salvaDossierWeb`), esattamente come per la nota vocale.
 */
export async function cercaDossierWeb(
  input: CercaDossierWebRequest,
): Promise<CercaDossierWebResponse> {
  const fn = httpsCallable<CercaDossierWebRequest, CercaDossierWebResponse>(
    functions,
    'cercaDossierWebRete',
  );
  // `stripUndefined` toglie i campi vuoti (Firestore/callable non li vogliono)
  // preservando il tipo: il nome resta obbligatorio.
  const result = await fn(stripUndefined({ ...input }));
  return result.data;
}

/**
 * Salva (o sostituisce) il dossier sulla scheda, in un campo SEPARATO dalle note.
 * `generato_il` viene dal client perché è il momento in cui l'utente l'ha accettato.
 */
export async function salvaDossierWeb(personaId: string, dossier: DossierWeb): Promise<void> {
  await updateDoc(doc(db, PERSONE_COLLECTION, personaId), {
    dossier_web: dossier,
    updated_at: serverTimestamp(),
  });
}

/** Rimuove il dossier dalla scheda (le note dell'utente non vengono toccate). */
export async function eliminaDossierWeb(personaId: string): Promise<void> {
  await updateDoc(doc(db, PERSONE_COLLECTION, personaId), {
    dossier_web: null,
    updated_at: serverTimestamp(),
  });
}

// ============================================================================
// COLLEGAMENTI TRA PERSONE
// ============================================================================

/**
 * Collega due persone. La scrittura è SIMMETRICA e ATOMICA: il legame compare su
 * entrambe le schede o su nessuna.
 *
 * ⚠️ Un legame scritto da una parte sola è peggio di nessun legame: la rubrica
 * direbbe cose diverse a seconda della scheda da cui la guardi, e la divergenza
 * non si noterebbe finché non serve.
 *
 * Idempotente: ricollegare due persone già collegate aggiorna tipo e nota invece
 * di creare un doppione.
 */
export async function collegaPersone(
  personaA: PersonaRete,
  personaB: PersonaRete,
  tipo?: string,
  nota?: string,
): Promise<void> {
  if (personaA.id === personaB.id) throw new Error('Non si può collegare una persona a sé stessa');

  const senza = (lista: CollegamentoRete[] | undefined, id: string) =>
    (lista ?? []).filter((c) => c.persona_id !== id);

  const legameA: CollegamentoRete = stripUndefined({ persona_id: personaB.id, tipo, nota });
  const legameB: CollegamentoRete = stripUndefined({ persona_id: personaA.id, tipo, nota });

  const batch = writeBatch(db);
  batch.update(doc(db, PERSONE_COLLECTION, personaA.id), {
    collegamenti: [...senza(personaA.collegamenti, personaB.id), legameA],
    updated_at: serverTimestamp(),
  });
  batch.update(doc(db, PERSONE_COLLECTION, personaB.id), {
    collegamenti: [...senza(personaB.collegamenti, personaA.id), legameB],
    updated_at: serverTimestamp(),
  });
  await batch.commit();
}

/** Rimuove il legame da ENTRAMBE le schede, in un'unica operazione. */
export async function scollegaPersone(
  personaA: PersonaRete,
  personaB: PersonaRete,
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, PERSONE_COLLECTION, personaA.id), {
    collegamenti: (personaA.collegamenti ?? []).filter((c) => c.persona_id !== personaB.id),
    updated_at: serverTimestamp(),
  });
  batch.update(doc(db, PERSONE_COLLECTION, personaB.id), {
    collegamenti: (personaB.collegamenti ?? []).filter((c) => c.persona_id !== personaA.id),
    updated_at: serverTimestamp(),
  });
  await batch.commit();
}

// ============================================================================
// PRESENTAZIONI — chi ha presentato chi (legame con una direzione)
// ============================================================================

/**
 * Segna che `presentatoreId` ha presentato `presentato` a Simone. Scrive SOLO
 * sulla scheda di chi è stato presentato (`presentato_da`): la scheda di chi
 * presenta lo ricava da lì, quindi non c'è una seconda copia da tenere allineata.
 *
 * Idempotente (`arrayUnion`): rifarlo non crea doppioni.
 */
export async function segnaPresentazione(presentato: PersonaRete, presentatoreId: string): Promise<void> {
  if (presentato.id === presentatoreId) throw new Error('Una persona non può presentare sé stessa');
  await updateDoc(doc(db, PERSONE_COLLECTION, presentato.id), {
    presentato_da: arrayUnion(presentatoreId),
    updated_at: serverTimestamp(),
  });
}

/** Toglie la presentazione (anche verso una scheda eliminata). */
export async function togliPresentazione(presentatoId: string, presentatoreId: string): Promise<void> {
  await updateDoc(doc(db, PERSONE_COLLECTION, presentatoId), {
    presentato_da: arrayRemove(presentatoreId),
    updated_at: serverTimestamp(),
  });
}
